import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../hooks/hooksRedux';

import { RootState } from '../../store';

import { formatCurrency } from '../../utils/formatCurrency';
import { CustomTooltip } from './CustomTooltip';
import {
  Container,
  Footer,
  Header,
  TotalValue
} from '../../pages/DashboardCustomer/OnboardingOffshore/CardInfo/styleGuide';
import { defaultBorder, defaultColors } from '../../styles/style-guide';

import { IUser } from '../../interfaces/entities';
import { mergeWithPriority } from '../../pages/Onboarding/utils/mergeWithPriority';
import { DataOnboarding } from '../../pages/Onboarding/Context/Interface';
import { toTitleCase } from '../../pages/DashboardCustomer/OnboardingOffshore/CardInfo/utils';

export interface IProduct {
  value: string | null;
  description: string;
  count?: number;
}

const formatCartDescription = (description: string, t: (key: string) => string): string => {
  const feeTranslationMap: Record<string, string> = {
    'incorporation fee': t('others.othIncorporationFee'),
    'government fee': t('others.othGovernmentFee'),
    director: t('others.othDirectors'),
    directors: t('others.othDirectors'),
    shareholders: t('others.othShareholders'),
    members: t('labels.lblMembers'),
    member: t('labels.lblMembers')
  };

  const normalized = (description || '').trim().toLowerCase();
  if (feeTranslationMap[normalized]) {
    return feeTranslationMap[normalized];
  }

  return toTitleCase(description || '');
};

type CartProps = {
  userJurisdiciton?: IUser;
  additionals?: IProduct[];
  removes?: string[];
  removeAddons?: boolean;
  hire?: boolean;
  dataOnboarding?: DataOnboarding;
  currentStep?: string;
};

const Cart: React.FC<CartProps> = (props) => {
  const [summary, setSummary] = useState<any | null>(null);
  const [productsRender, setProductsRender] = useState<any>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [titleJurisdiction, setTitleJurisdiction] = useState('');

  const { additionals, removeAddons = false, dataOnboarding } = props;
  const { t } = useTranslation();

  const { colors, border } = useAppSelector((state: RootState) => state.styleGuide.data);

  const primaryColor = colors.primary || defaultColors.primary;
  const primaryBorderColor = border.primary || defaultBorder.primary;

  useEffect(() => {
    if (dataOnboarding) {
      const selectedAddons = additionals?.filter((addon) => addon.value !== '0') ?? [];
      setTitleJurisdiction(dataOnboarding?.jurisdiction?.description || '');
      setSummary({ ...dataOnboarding, additionals: selectedAddons });
    }
  }, [dataOnboarding, additionals]);

  useEffect(() => {
    
    if (!summary) return;

    // Filtrando os produtos relevantes
    const selectedAddons = additionals?.filter((addon) => addon.value !== '0' || (addon.count ?? 0) > 0) ?? [];
    const mergedProducts = mergeWithPriority(selectedAddons, summary.fees, removeAddons ? [] : []);

    let products = mergedProducts.reduce((acc: IProduct[], item: any) => {
      const { description, value } = item;
      let { count } = item;
      const descriptionKey = description?.toLowerCase() ?? '';

      if (descriptionKey === 'directors') {
        count = summary?.directors?.number_of_directors ?? 0;
      }

      const existingItemIndex = acc.findIndex((i) => i.description?.toLowerCase() === descriptionKey);
      if (existingItemIndex !== -1) {
        acc[existingItemIndex].count = (acc[existingItemIndex].count ?? 0) + (count || 0);
        acc[existingItemIndex].value = acc[existingItemIndex].value || value;
      } else {
        acc.push({ ...item, description });
      }
      return acc;
    }, []);

    console.log('Produtos antes de filtro:', products);
    console.log(t('others.othCorporateDirectorOption').toLocaleLowerCase());
    

    const hasCorporateDirector = products.some(
      (item) => item.description?.toLowerCase() === t('others.othCorporateDirectorOption').toLocaleLowerCase() && item.value !== ''
    );

    if (hasCorporateDirector) {
      products = products.filter((item) => item.description.toLowerCase() !== t('others.othDirectorOption').toLocaleLowerCase());
    }

    // Filtrando apenas os produtos válidos
    const filteredProducts = products.filter((item) => item.value !== '' || (item.count ?? 0) > 0);

    console.log('Produtos filtrados:', filteredProducts);

    // Descrição vem da API já no idioma correto (Content-Language na requisição)
    setProductsRender(
      filteredProducts.map((item, index) => (
        <div key={index} className="flex justify-between gap-0 border-b-2 px-6 py-2">
          <div>
            <span className="text-sm text-[#585858]">{formatCartDescription(item.description || '', t)}</span>
            {item.count !== undefined && item.count !== null && (
              <span className="ml-2 text-sm text-[#585858]">{item.count}</span>
            )}
          </div>
          <span style={{ color: primaryColor }} className="text-sm font-bold">
            {item.value !== null ? formatCurrency(Number(item.value)) : ''}
          </span>
        </div>
      ))
    );

    // Calculando o total baseado no `filteredProducts`
    const total = filteredProducts.reduce(
      (acc, item) => acc + Number(item.value ?? 0),
      0
    );

    console.log('Novo total calculado:', total);
    setTotalPrice(total);
  }, [summary, additionals, removeAddons, t]);
  return (
    <Container>
      <Header>{`${t('PackageTitle')} ${titleJurisdiction}`}</Header>

      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[690px]">
          {productsRender}

          {(dataOnboarding?.corporate_directors?.number_of_corporate_director ?? 0) > 0 &&
            (dataOnboarding?.corporate_directors?.values ?? 0) > 0 &&
            props.hire &&
            !productsRender.some(
              (item: any) =>
                typeof item.props.children[0]?.props?.children[0] === 'string' &&
                item.props.children[0].props.children[0].toLowerCase().includes('corporate directors')
            ) && (
              <div className="flex justify-between gap-0 border-b-2 px-6 py-2">
                <div>
                  <span className="text-sm capitalize text-[#585858]">{t('descriptions.CorporateDirector')}</span>
                  <span className="ml-2 text-sm text-[#585858]">
                    {dataOnboarding?.corporate_directors?.number_of_corporate_director}
                  </span>
                </div>
                <span style={{ color: primaryColor }} className="text-sm font-bold">
                  {dataOnboarding?.corporate_directors && formatCurrency(dataOnboarding.corporate_directors.values)}
                </span>
              </div>
            )}
        </div>
      </div>

      <Footer $borderColor={primaryBorderColor}>
        <TotalValue color={primaryColor}>
          {t('others.othTotal')}: {totalPrice !== undefined ? formatCurrency(totalPrice) : formatCurrency(0)}
        </TotalValue>
      </Footer>
    </Container>
  );
};

export { Cart };
